import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft } from 'lucide-react';
import { API, LOGO_DARK } from '../config';
import { setSeo } from '../lib/seo';

const SITE = 'https://app.dailydiscipline.app';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch (e) { return ''; }
};

const mdComponents = {
  h1: (p) => <h2 className="text-2xl font-bold text-neutral-900 mt-10 mb-3" {...p} />,
  h2: (p) => <h2 className="text-2xl font-bold text-neutral-900 mt-10 mb-3" {...p} />,
  h3: (p) => <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-2" {...p} />,
  p: (p) => <p className="text-neutral-700 leading-relaxed mb-5 text-lg" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 mb-5 space-y-2 text-neutral-700" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 mb-5 space-y-2 text-neutral-700" {...p} />,
  li: (p) => <li className="text-lg leading-relaxed" {...p} />,
  a: (p) => <a className="text-emerald-600 underline hover:text-emerald-800" {...p} />,
  blockquote: (p) => <blockquote className="border-l-4 border-emerald-300 pl-5 italic text-neutral-600 my-6 text-lg" {...p} />,
  strong: (p) => <strong className="font-semibold text-neutral-900" {...p} />,
  hr: () => <hr className="my-8 border-neutral-200" />,
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        const p = res.data;
        setPost(p);
        setSeo({
          title: `${p.seo_title || p.title} — Daily Discipline`,
          description: p.seo_description || p.excerpt,
          canonical: `${SITE}/blog/${p.slug}`,
          url: `${SITE}/blog/${p.slug}`,
          image: p.cover_image || `${SITE}/og-image.png`,
          type: 'article',
          jsonld: {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: p.title,
            description: p.seo_description || p.excerpt,
            image: p.cover_image || `${SITE}/og-image.png`,
            datePublished: p.published_at,
            dateModified: p.updated_at,
            author: { '@type': 'Person', name: p.author || 'Pam Matambanadzo' },
            publisher: { '@type': 'Organization', name: 'Daily Discipline', logo: { '@type': 'ImageObject', url: LOGO_DARK } },
            mainEntityOfPage: `${SITE}/blog/${p.slug}`,
          },
        });
      } catch (e) {
        setNotFound(true);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>;
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-2xl font-bold text-neutral-900">Post not found</h1>
        <Link to="/blog" className="text-emerald-600 font-semibold">← Back to the blog</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/blog')} data-testid="back-to-blog" className="inline-flex items-center gap-2 text-neutral-600 hover:text-emerald-600 font-medium text-sm">
            <ArrowLeft size={18} /> All posts
          </button>
          <Link to="/"><img src={LOGO_DARK} alt="Daily Discipline" className="h-8 w-8" /></Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="blog-post-page">
        {post.tags?.length > 0 && (
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-600">{post.tags[0]}</span>
        )}
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 mt-2 leading-tight">{post.title}</h1>
        <div className="flex items-center gap-3 text-sm text-neutral-500 mt-4">
          <span className="font-medium text-neutral-700">{post.author}</span>
          <span>·</span>
          <span>{fmtDate(post.published_at)}</span>
        </div>

        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="w-full rounded-2xl mt-8 object-cover max-h-[420px]" />
        )}

        <div className="mt-8">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {post.body || ''}
          </ReactMarkdown>
        </div>

        {/* Conversion CTA */}
        <div className="mt-12 bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-100 p-8 text-center">
          <h3 className="text-xl font-bold text-neutral-900">Ready to build discipline, without the guilt?</h3>
          <p className="text-neutral-600 mt-2">Daily Discipline is your AI habit coach — one tiny win at a time.</p>
          <Link to="/" data-testid="blog-cta-app" className="inline-block mt-5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full px-7 py-3.5 transition">
            Start free →
          </Link>
        </div>
      </article>

      <footer className="border-t border-neutral-200 mt-12 py-8 text-center text-sm text-neutral-400">
        © {new Date().getFullYear()} Daily Discipline™ ·{' '}
        <Link to="/blog" className="hover:text-neutral-600">Blog</Link> ·{' '}
        <Link to="/legal/privacy" className="hover:text-neutral-600">Privacy</Link>
      </footer>
    </div>
  );
};

export default BlogPost;
